import { Component, ElementRef, EventEmitter, Inject, Input, OnDestroy, OnInit, Output, QueryList, ViewChildren } from '@angular/core';
import { APP_BASE_HREF } from '@angular/common';

import { merge, Observable, filter, Subscription, take, combineLatest, BehaviorSubject } from 'rxjs';
import { BlobType } from '@zumer/snapdom';

import { EntityType } from '../../../../api/targeting/enum/entity.type.enum';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { ChartExportEventModel } from '../../../../Common/Charts/models/chart-export-event.model';
import { SocialChartExportModel, SocialChartExportService } from '@Social/Intelligence/services/social-chart-export.service';
import { Am5DateValueXYChart } from '../../../../Common/Charts/models/Am5DateValueXYChart/am5-date-value-x-y-chart.model';
import { Am5ChartTooltipService } from '../../../../Common/Charts/services/am5-chart-tooltip.service';
import { Am5ChartConfigurationService } from '../../../../Common/Reports/services/am5-chart-configuration.service';
import { UserCurrency } from '../../../../Core/Users/enums/user-currency';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';

import { Am5Chart } from '../../../../Common/Charts/models/am5-chart.model';
import { AgprComponentStyleEnum } from '../../../../Common/Basic/enums/social-analysis-types.enum';
import { CardChartComponentHighlightColorEnum, CardChartComponentLayoutEnum } from '../../../../Common/Charts/components/CardChart/card-chart.component';
import { ChartModel } from '../../../../Common/Charts/models/chart.model';
import { DropdownTypesEnum } from '../../../../Common/Forms/enums/dropdown-types.enum';
import { ReportBlockTypeEnum } from '../../../../Common/Reports/enums/report-block-type.enum';

import { ReportSeriesModel } from '../../../../Common/Reports/models/report-series.model';
import { ReportsChartDetailService } from '../../../../Common/Reports/services/reports-chart-detail.service';
import { ReportsChartSeriesConverterService } from '../../../../Common/Reports/services/reports-chart-series-converter.service';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { TopicsService } from '../../../../Monitor/Core/services/topics.service';
import { SentimentModel } from '../../../../Monitor/Shared/models/sentiment.model';
import { SocialSeriesMetricsEnum } from '@Social/Intelligence/enums/social-series-metrics.enum';
import { VISeriesTypesEnum, VISummarySeriesTypesEnum } from '../../../enums/vi-series-types.enum';
import { ViSummaryChartService } from '../../../services/vi-summary-chart.service';
import { VISeriesMap } from '../../../services/vi-charts.service';
import { ViReportsSearchService } from '../../../services/vi-reports-search.service';
import { ViMonitorService } from '../../../services/vi-monitor.service';

import { ChartStylesModel } from '../../../../Common/Charts/models/chart-styles.model';
import { ChartSpecificTypesEnum } from '../../../../Common/Reports/enums/chart-specific-types.enum';
import { ChartTimeIntervalsEnum } from '../../../../Common/Reports/enums/chart-time-intervals.enum';
import { Am5ChartSettingsService } from '../../../../Common/Reports/services/am5-chart-settings.service';
import { ChartDisplayElementModel } from '../../../../Common/Reports/models/chart-display-element.model';
import { ReportDateRangeModel } from '../../../../Common/Reports/models/report-date-range.model';
import {
    VisibilityIntelligenceSummaryChartTypesEnum,
    viSummaryCardChartMap,
    viSummaryCardChartPPMap,
    viSummaryTrendChartMap,
    viSummaryColorMap,
    viSummaryCardColorMap,
    viNeoFacetsToChartTypeSummaryTrendChartMap
} from '../../../enums/vi-summary-chart-types.enum';
import { DateQueryModel } from '../../../../Common/Forms/models/date-query.model';
import { Am5DateValueXYChartConfiguration } from '../../../../Common/Charts/models/Am5DateValueXYChart/am5-date-value-x-y-chart-configuration.model';
import { ViMonitor } from '../../../models/vi-monitor.model';
import { ViMonitorStatusEnum } from '../../../enums/vi-monitor-status.enum';
import { CategoryValueXYChart } from '@Common/Charts/models/category-value-x-y-chart.model';
import { CategoryValueXYChartOptions } from '@Common/Charts/models/barChart/category-value-x-y-chart-options.model';
import { ChartMetadataModel } from '@Common/Charts/models/chart-metadata.model';
import { LabelDisplayOptionsEnum } from '@Common/Misc/enums/label-display-options.enum';
import { IndividualReportSeriesConfig } from '../../../services/vi-reports-search.service';
import { userAppPreferencesDefault } from '@Core/Users/data/user-app-preferences-default';

import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '../../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../../Core/Dialogs/enums/overlay-animation.enum';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { ViChartDrillDownDialogComponent } from '../../ViChartDrillDownDialog/vi-chart-drill-down.dialog.component';
import { DateQueryTypeEnum } from '@api/mentions/enum/date-query-type.enum';

interface SummaryChartGroupings {
    chartType: VisibilityIntelligenceSummaryChartTypesEnum;
    exportID: string;
    header: string;
    message: string;

    cardSeries: ReportSeriesModel[];
    cardChart?: CategoryValueXYChart;
    cardColor?: CardChartComponentHighlightColorEnum;
    cardBubbleTooltip?: string;
    cardMetric?: VISummarySeriesTypesEnum;
    decimalNumber?: number;
    cardBarChartHeightClass: string;

    lineChart: Am5DateValueXYChart;
    lineChartSeries: ReportSeriesModel[];
}

@Component({
    selector: 'agpr-vi-summary',
    templateUrl: './vi-summary.component.html',
    styleUrls: ['./vi-summary.component.scss'],
    providers: [Am5ChartTooltipService],
    standalone: false
})
export class VISummaryComponent implements OnInit, OnDestroy {
    @Input() loading: boolean = false;

    @Output() reportSeriesReady: EventEmitter<VISeriesMap> = new EventEmitter<VISeriesMap>();
    @Output() reportSeriesChange: EventEmitter<{oldSeriesTypes: any[]; newSeries: ReportSeriesModel[]}> = new EventEmitter();

    monitor: ViMonitor;
    seriesMap: VISeriesMap;
    classToChartModelMap: Map<string, ChartModel | Am5Chart>;
    classToReportSeriesMap: Map<string, ReportSeriesModel[]>;

    baseSentiments: SentimentModel[] = [];
    currencyCode: UserCurrency;
    hasData: boolean = true;

    dateQuery: DateQueryModel = new DateQueryModel();

    percentageCharts: VisibilityIntelligenceSummaryChartTypesEnum[] = [
        VisibilityIntelligenceSummaryChartTypesEnum.PRESENCE_RATE,
        VisibilityIntelligenceSummaryChartTypesEnum.RISK_RATE,
    ];

    summaryCharts: SummaryChartGroupings[] = [];
    subscriptions: Subscription[] = [];
    competiorConfigs: IndividualReportSeriesConfig[] = [];

    @ViewChildren('viTrendElement') viTrendElements!: QueryList<ElementRef>;

    private overlayContainer: Element;

    readonly id: string = 'vi_summary';

    readonly defaultChartColors = userAppPreferencesDefault.reportsPrefs.defaultChartColors;
    readonly cardChartComponentHighlightColors = CardChartComponentHighlightColorEnum;
    readonly socialMetrics = SocialSeriesMetricsEnum;
    readonly cardChartLayoutTypes = CardChartComponentLayoutEnum;
    readonly dropdownTypes = DropdownTypesEnum;
    readonly agprComponentStyles = AgprComponentStyleEnum;
    readonly entityType = EntityType;
    readonly agprComponentStyle = AgprComponentStyleEnum;
    readonly monitorStatus = ViMonitorStatusEnum;

    readonly messageSeverities = messageSeverityEnum;
    readonly VISummarySeriesTypes = VISummarySeriesTypesEnum;
    readonly viSummaryChartTypeEnum = ReportBlockTypeEnum;
    readonly supportedExportFormats = [ 'png', 'jpg', 'svg', 'json', 'csv', 'print' ];

    constructor(
        public viSummaryChartService: ViSummaryChartService,
        public reportsChartSeriesConverterService: ReportsChartSeriesConverterService,
        public reportsChartDetailService: ReportsChartDetailService,
        public topicsService: TopicsService,
        public viSearchService: ViReportsSearchService,
        public localizationService: LocalizationService,
        public chartExportService: SocialChartExportService,
        public am5ChartConfigurationService: Am5ChartConfigurationService,
        public am5ChartSettingsService: Am5ChartSettingsService,
        public am5ChartTooltipService: Am5ChartTooltipService,
        public currentUserStateService: CurrentUserStateService,
        private viMonitorService: ViMonitorService,
        private coreDialogService: CoreDialogService,
        @Inject (APP_BASE_HREF) public appBaseHref: string
    ) {
        this.overlayContainer = document.getElementById('main-content');
    }

    openAm5ChartDrilldown(event: any) {
        const url = event.data;
        if (!url) {
            return;
        }

        let drilldownInfo: any;
        try {
            drilldownInfo = JSON.parse(decodeURIComponent(atob(url?.split('drilldown=')?.[1])));
        } catch (e) {
            console.log('drilldownInfo not correct', e);
        }

        if (!drilldownInfo) {
            return;
        }

        const dateQuery = new DateQueryModel();
        dateQuery.type = DateQueryTypeEnum.ABSOLUTE;
        dateQuery.absoluteRange = {
            start: new Date(drilldownInfo.rawStartDate),
            end: new Date(drilldownInfo.rawEndDate)
        };
        dateQuery.timezone = 'UTC';

        const chartType = viNeoFacetsToChartTypeSummaryTrendChartMap.get(drilldownInfo.key);
        const chartGrouping = this.summaryCharts.find(chart => chart.chartType === chartType);

        let cardSeries: ReportSeriesModel[] = [];
        if (chartType === VisibilityIntelligenceSummaryChartTypesEnum.COMPETITIVE_STRENGTH) {
            this.competiorConfigs.filter(
                config => config.type === VISummarySeriesTypesEnum.competitiveScoreTrend
            ).forEach(cardConfig => cardSeries.push(this.seriesMap.get(cardConfig.dynamicKey as unknown as VISeriesTypesEnum)?.clone()));
        } else {
            cardSeries = [viSummaryCardChartMap.get(chartType), viSummaryCardChartPPMap.get(chartType)].map(x => this.seriesMap.get(x)?.clone())
        }

        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );

        const io = {
            inputs: {
                animationTrigger: animationTrigger,
                overlayContainer: this.overlayContainer,
                chartType,
                dateQuery,
                cardSeries,
                chartLabel: chartGrouping?.header
            },
            outputs: {}
        };

        this.coreDialogService.open(ViChartDrillDownDialogComponent, io);
    }

    ngOnInit(): void {
        const resourceLoaded$: Observable<any> = merge(this.viMonitorService.currentResource$).pipe(
            filter(x => !!x),
        );
        this.subscriptions.push(
            resourceLoaded$.subscribe((monitor) => {
                this.monitor = monitor;
                if (this.monitor?.isCompetitiveBenchmarking) {
                    this.competiorConfigs = this.viSummaryChartService.createDynamicCompetitiveChart(this.monitor);
                }
                this.viSummaryChartService.setChartConfig([...this.viSummaryChartService.VISummarySeriesConfig, ...this.competiorConfigs]);
            }),
            this.currentUserStateService.getUserCurrency().subscribe(x => this.currencyCode = x),
            this.viSummaryChartService.getHasData().subscribe(x => this.hasData = x),
            combineLatest([
                this.viSearchService.getDateQuery().pipe(take(1)),
                this.viSummaryChartService.getSeriesMap().pipe(filter(x => !!x)),
            ]).subscribe(([dateQuery, seriesMap]) => {

                this.dateQuery = dateQuery;
                this.seriesMap = seriesMap;

                this.setupCharts();
                this.reportSeriesReady.emit(this.seriesMap);
                this.preCacheSnapDomExports();
            })
        );
    }

    /*
        this is to help generate exports faster, we preCache them, so snapdom doesn't do a full load on export
    */
    preCacheSnapDomExports(): void {
        setTimeout(() => {
            this.viTrendElements.forEach(element =>
                this.chartExportService.preCacheSnapDom(element.nativeElement)
            );
        }, 1000);
    }

    setupCharts(): void {
        this.summaryCharts = [];
        for (const value of Object.values(VisibilityIntelligenceSummaryChartTypesEnum)) {
            let summaryCardSeries: ReportSeriesModel[] = [];
            let summaryTrendSeries: ReportSeriesModel[] = [];
            let summaryCardChart: CategoryValueXYChart = null;
            let defaultClassHeight = '';

            let trendChartType = ReportBlockTypeEnum.COVERAGE_OVER_TIME;

            this.am5ChartTooltipService.setDoLink(true);
            this.am5ChartTooltipService.setCustomTooltipLink('VisibilityIntelligence.reports.tooltip.label');
            if (value === VisibilityIntelligenceSummaryChartTypesEnum.COMPETITIVE_STRENGTH) {
                this.am5ChartTooltipService.setDoLink(false);
                trendChartType = ReportBlockTypeEnum.COVERAGE_OVER_TIME_COMPARISON;
                if (!this.competiorConfigs?.length || !this.monitor?.isCompetitiveBenchmarking) {
                    continue;
                }
                this.competiorConfigs.filter(
                    config => config.type === VISummarySeriesTypesEnum.competitiveScoreAvg
                ).forEach(cardConfig => summaryCardSeries.push(this.seriesMap.get(cardConfig.dynamicKey as unknown as VISeriesTypesEnum)));
                this.competiorConfigs.filter(
                    config => config.type === VISummarySeriesTypesEnum.competitiveScoreTrend
                ).forEach(cardConfig => summaryTrendSeries.push(this.seriesMap.get(cardConfig.dynamicKey as unknown as VISeriesTypesEnum)));

                const chartStyles = new ChartStylesModel();

                chartStyles.colorMap = {
                    'seriesColor0': this.defaultChartColors?.generalColors?.color1,
                    'seriesColor1': this.defaultChartColors?.generalColors?.color2,
                    'seriesColor2': this.defaultChartColors?.generalColors?.color3,
                    'seriesColor3': this.defaultChartColors?.generalColors?.color4,
                    'seriesColor4': this.defaultChartColors?.generalColors?.color5,
                    'seriesColor5': this.defaultChartColors?.generalColors?.color6,
                    'seriesColor6': this.defaultChartColors?.generalColors?.color7,
                    'seriesColor7': this.defaultChartColors?.generalColors?.color8,
                    'seriesColor8': this.defaultChartColors?.generalColors?.color9,
                    'seriesColor9': this.defaultChartColors?.generalColors?.color10,
                    'seriesColor10': this.defaultChartColors?.generalColors?.color11,
                    'seriesColor11': this.defaultChartColors?.generalColors?.color12,
                };
                chartStyles.columnHeightThreshold = 12;
                chartStyles.customPadding = {
                    left: 10,
                    top: 0,
                    right: 0,
                    bottom: 0
                };

                summaryCardChart = new CategoryValueXYChart(
                    summaryCardSeries,
                    {
                        chartSpecificType: ChartSpecificTypesEnum.BAR,
                        categoryLabelStackedOnTop: true,
                        hideLegend: true,
                        noActionOnColumnClick: true,
                        reverseBarSeriesOrder: true,
                        dataPointDistinctColor: true,
                        useReportSeriesColor: true,
                        styles: chartStyles,
                    } as CategoryValueXYChartOptions,
                    {
                        type: ReportBlockTypeEnum.SHARE_OF_VOICE,
                        enableDataDisplaySettings: true,
                        ignoreForcingLabel: true,
                        labelDisplayOptions: [LabelDisplayOptionsEnum.count]
                    } as ChartMetadataModel,
                    [{}]
                );

                if (summaryCardSeries?.length > 10) {
                    defaultClassHeight = 'agpr-viSummary-container-left-largest';
                } else if (summaryCardSeries?.length > 8) {
                    defaultClassHeight = 'agpr-viSummary-container-left-large';
                } else if (summaryCardSeries?.length > 5) {
                    defaultClassHeight = 'agpr-viSummary-container-left-medium';
                } else if (summaryCardSeries?.length > 3) {
                    defaultClassHeight = 'agpr-viSummary-container-left-small';
                } else if (summaryCardSeries?.length > 1) {
                    defaultClassHeight = 'agpr-viSummary-container-left-smallest';
                }
            } else {
                summaryCardSeries = [viSummaryCardChartMap.get(value), viSummaryCardChartPPMap.get(value)].map(x => this.seriesMap.get(x));
                summaryTrendSeries = [this.seriesMap.get(viSummaryTrendChartMap.get(value))];
            }

            const summaryTrendLineStyles = new ChartStylesModel();
            summaryTrendLineStyles.colorMap = {'analysisColor1': viSummaryColorMap.get(value)};

            const chartSeries = this.reportsChartSeriesConverterService.getDateValueChartSeries(
                summaryTrendSeries, trendChartType, ChartSpecificTypesEnum.MULTI_LINE, this.currencyCode
            );
            for (const trendSeries of summaryTrendSeries) {
                trendSeries.dateRange = new ReportDateRangeModel().setFromDateQuery(this.dateQuery);
                trendSeries.setChartType(trendChartType);
            }

            const chartDisplayElement = new ChartDisplayElementModel('', summaryTrendSeries, summaryTrendLineStyles);
            chartDisplayElement.chartSpecificType = ChartSpecificTypesEnum.LINE;
            chartDisplayElement.timeInterval = ChartTimeIntervalsEnum.DYNAMIC;
            chartDisplayElement.series = summaryTrendSeries;
            const chartConfiguration = this.am5ChartConfigurationService.getChartConfiguration(this.id, trendChartType);
            chartConfiguration.noContent.isMessageStyle = true;
            (chartConfiguration as Am5DateValueXYChartConfiguration).useScrollbar = false;
            (chartConfiguration as Am5DateValueXYChartConfiguration).hasLegend = summaryTrendSeries?.length > 1;

            if ((value === VisibilityIntelligenceSummaryChartTypesEnum.RISK_RATE) ||
                (value === VisibilityIntelligenceSummaryChartTypesEnum.PRESENCE_RATE) ||
                (value === VisibilityIntelligenceSummaryChartTypesEnum.BRAND_SCORE) ||
                (value === VisibilityIntelligenceSummaryChartTypesEnum.COMPETITIVE_STRENGTH) ||
                (value === VisibilityIntelligenceSummaryChartTypesEnum.VISIBILITY_SCORE) ||
                (value === VisibilityIntelligenceSummaryChartTypesEnum.SENTIMENT_SCORE) ||
                (value === VisibilityIntelligenceSummaryChartTypesEnum.ACCURANCY_SCORE)
            ) {
                (chartConfiguration as Am5DateValueXYChartConfiguration).maxYValue = 100;
            }

            if ((value === VisibilityIntelligenceSummaryChartTypesEnum.RISK_RATE) || (value === VisibilityIntelligenceSummaryChartTypesEnum.PRESENCE_RATE)) {
                chartSeries[0].percentageValueAxis = true;
            }

            if (value === VisibilityIntelligenceSummaryChartTypesEnum.RANK_WHEN_LISTED) {
                (chartConfiguration as Am5DateValueXYChartConfiguration).forceInverseMinMax = true;
            }

            const chartSettings = this.am5ChartSettingsService.getChartSettings(
                trendChartType,
                chartDisplayElement
            );

            chartSeries.forEach((x, i) => {
                x.tooltipHTML = this.am5ChartTooltipService.getChartTooltip([x], chartConfiguration, chartSettings, i, false);
            });

            // have to hardcode for now, might need to change on the fly after date change
            (chartSettings as any).dateGranularity = {timeUnit: 'day', count: 1};
            (chartSettings as any).height = 400;

            const summaryTrendLineChart = new Am5DateValueXYChart(
                chartSeries,
                chartConfiguration,
                chartSettings
            );

            this.summaryCharts.push({
                chartType: value,
                exportID: `viSummary_${value}_export`,
                header: `Monitor.VISummarySeriesTypes.summary.${value}.title`,
                message: `Monitor.VISummarySeriesTypes.summary.${value}.message`,

                cardChart: summaryCardChart,
                cardSeries: summaryCardSeries,
                cardColor: viSummaryCardColorMap.get(value),
                cardBubbleTooltip: 'Monitor.cardCharts.bubbleTooltipSubject',
                cardMetric: viSummaryCardChartMap.get(value),
                decimalNumber: (value === VisibilityIntelligenceSummaryChartTypesEnum.RANK_WHEN_LISTED) ? 2 : null,
                cardBarChartHeightClass: defaultClassHeight,

                lineChart: summaryTrendLineChart,
                lineChartSeries: summaryTrendSeries,
            });
        };
    }

    async summaryTrendExport(event: ChartExportEventModel, elementRef: HTMLElement, chartType?: ReportBlockTypeEnum, label?: string) {
        await this.chartExportService.exportImageSnapDomWithOverrides(
            event.eventKey.toLowerCase() as BlobType,
            elementRef,
            this.prepareExportData(chartType, label).title,
            [ { selector: '.agpr-viSummary-exportMenu', style: { display: 'none' } } ]
        );
    }

    private prepareExportData(chartType: ReportBlockTypeEnum, label: string): SocialChartExportModel {
        switch (chartType) {
            case ReportBlockTypeEnum.COVERAGE_OVER_TIME_COMPARISON:
            case ReportBlockTypeEnum.COVERAGE_OVER_TIME:
                return {
                    title: this.localizationService.transform(label),
                };
            default:
                break;
        }
        return;
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(x => x.unsubscribe());
    }
}
